import Vue from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import { useSectionsStore } from "@/stores/sections";
import { useNavigationStore } from "@/stores/navigation";

import Tile from "@/models/Tiles/Tile";

export const tiles_state = {
  tilesMap: {},
  sectionTilesMap: {},
  tilesCatalog: []
};

export const tiles_actions = {
  /**
   * Load tiles for dashboard if not present
   * @param { string } collectionId Id of tiles collection
   */
  async loadTiles(collectionId = "UserMainTiles") {
    if (this.tilesMap[collectionId]) {
      return;
    }

    await this.reloadTiles(collectionId);

    // Check if UserMainTiles is available - else load it to get base
    if (!this.tilesMap["UserMainTiles"]) {
      await this.reloadTiles("UserMainTiles");
    }
  },

  /**
   * Force load tiles for dashboard
   * @param { string } collectionId Id of tiles collection
   */
  async reloadTiles(collectionId) {
    const response = await apiSkobo.get(`/Tile/${collectionId}`);
    const tilesList = response.data.values.map(t => Tile.parseFromApi(t));
    Vue.set(this.tilesMap, collectionId, tilesList);
  },

  /**
   * Load tiles for section
   * @param { string } sectionId Id of section to load tiles for
   * @param { string } collectionId Id of tiles collection
   * @returns { Promise<void> } Promise
   */
  async loadTilesForSection(sectionId, collectionId) {
    if (this.sectionTilesMap[sectionId + collectionId]) {
      return;
    }

    return await this.reloadTilesForSection(sectionId, collectionId);
  },

  async reloadTilesForSection(sectionId, collectionId) {
    const tilesForSectionResponse = await apiSkobo.get(
      `/Tile/Section/${
        (sectionId !== undefined ? sectionId : "") + (collectionId !== undefined ? "/" + collectionId : "")
      }`
    );

    const tilesList = tilesForSectionResponse?.data?.values?.map(t => Tile.parseFromApi(t));

    if (!this.sectionTilesMap[sectionId]) {
      Vue.set(this.sectionTilesMap, sectionId, {});
    }

    Vue.set(this.sectionTilesMap[sectionId], parseMenuId(collectionId), tilesList);
  },

  /**
   * Load tiles for all my sections
   * @param { string } collectionId Id of tiles collection
   */
  async loadTilesForMySections(collectionId) {
    const sectionsStore = useSectionsStore();
    const mySectionsIds = Object.keys(sectionsStore.mySections);

    mySectionsIds.forEach(sectionId => {
      this.loadTilesForSection(sectionId, collectionId);
    });
  },

  /**
   * Load tiles for all archived my sections
   * @param { string } collectionId Id of tiles collection
   */
  async loadTilesForMyArchivedSections(collectionId) {
    const sectionsStore = useSectionsStore();
    const mySectionsIds = Object.keys(sectionsStore.myArchivedSections);

    mySectionsIds.forEach(sectionId => {
      this.loadTilesForSection(sectionId, collectionId);
    });
  },

  /**
   * Load entire tiles catalog
   */
  async loadCatalog() {
    try {
      if (this.tilesCatalog.length === 0) {
        const response = await apiSkobo.get("/Tile/Catalog/All");

        const catalogList = response.data.groups.map(g => ({
          title: g,
          items: response.data.values.filter(v => v.groupName === g)
        }));

        this.tilesCatalog = catalogList;
      }
    } catch {
      // eslint-disable-next-line no-empty
    }
  },

  /**
   * Load entire tiles hierarchy (UserMainTiles and all tiles that are linked to it)
   */
  async loadEntireTilesHierarchy() {
    // Load UserMainTiles
    await this.loadTiles();
    this.tilesMap["UserMainTiles"].forEach(async tile => {
      if (tile.uri.uri.startsWith("/tiles/")) {
        await this.loadTiles(tile.uri.uri.replace("/tiles/", ""));
      }
    });
  }
};

/**
 * Parse menuid - if set return it - if not return "root"
 * @param { string } menuId Id of menu
 * @returns { string }  Reel menu id
 */
function parseMenuId(menuId) {
  return !menuId ? "root" : menuId;
}

export const tiles_getters = {
  sectionTiles: state => (sectionId, collectionId) =>
    state.sectionTilesMap[sectionId] ? state.sectionTilesMap[sectionId][parseMenuId(collectionId)] || [] : [],

  tiles: state => collectionId => {
    const id = collectionId ? collectionId : "UserMainTiles";
    return state.tilesMap[id] ? state.tilesMap[id] : [];
  },

  groupTitle: state => collectionId => {
    const linksToGroup = [];

    // Look in tiles
    for (let key in state.tilesMap) {
      linksToGroup.push(
        ...state.tilesMap[key].filter(
          v =>
            v.uri &&
            v.uri.action === "app" &&
            (v.uri.uri === "/tiles/" + collectionId || v.uri.uri === "tiles/" + collectionId)
        )
      );
    }

    if (linksToGroup.length > 0) {
      return linksToGroup[0].description;
    }

    // Look in navigation menu (should probably be merged with navigation and tiles)
    const navigationStore = useNavigationStore();
    linksToGroup.push(
      ...navigationStore.leftMenu.filter(
        v =>
          v.uri &&
          v.uri.action === "app" &&
          (v.uri.uri === "/tiles/" + collectionId || v.uri.uri === "tiles/" + collectionId)
      )
    );

    if (linksToGroup.length > 0) {
      return linksToGroup[0].description;
    }

    return "{Menu.Home}";
  },

  catalog: state => state.tilesCatalog,

  tilesBreadCrumbFromUri: state => uri => {
    return searchForTileInStateTiles(state.tilesMap, uri) || [];
  }
};

/**
 * Search for tile in state tiles
 * @param { object } map Tiles map to search
 * @param { string } uri Uri of tile to search for
 * @returns { Array } Array of breadcrumb items
 */
function searchForTileInStateTiles(map, uri) {
  const mapKeys = Object.keys(map);

  for (let i = 0; i < mapKeys.length; i++) {
    const tiles = map[mapKeys[i]];
    const tile = tiles.find(t => uri.startsWith(t.uri.uri));

    if (tile) {
      if (mapKeys[i] === "UserMainTiles" || mapKeys[i] === "UserSectionTiles") {
        return [
          {
            name: tile.title,
            routeName: "tiles",
            routeParams: { groupId: tile.uri.uri.replace("/tiles/", "") }
          }
        ];
      }
      const parents = searchForTileInStateTiles(map, `/tiles/${mapKeys[i]}`);
      parents.push({
        name: tile.title,
        routeName: "tiles",
        routeParams: { groupId: mapKeys[i] }
      });
      return parents;
    }
  }
  return [];
}
